'use client';
import {
  CrmTableIcon,
  KitchenDetailsIcon,
  LocationIcon,
} from '@/_assets/icon-components';
import Home2Icon from '@/_assets/icon-components/Home2';
import NumberToPrice from '@/_components/NumberToPrice';
import { useCustomLocale } from '@/_hooks/useCustomLocale';
import { ISingleOrder } from '@/_service/types/orders';
import numToPrice from '@/_utils/numToPrice';
import { Box, Flex, HStack, Stack, Text } from '@chakra-ui/react';
import { useTranslations } from 'next-intl';

const Mobile = ({
  order,
  total,
  change,
}: {
  order: ISingleOrder;
  total: number;
  change: number;
}) => {
  const t = useTranslations();
  const currentLocale = useCustomLocale();

  return (
    <Box>
      <Text px={4} fontSize='lg' fontWeight={700} lineHeight='24px'>
        {t('delivery_and_payment')}
      </Text>
      <Stack mt={4} gap={0}>
        {order?.delivery_type === 'self-pickup' ||
        order?.delivery_type === 'hall' ? (
          <Flex gap={4} px={4} py={'11px'} alignItems={'center'}>
            <LocationIcon color='#676767' />
            <Box>
              <Text color={'text.muted'} fontSize={'xs'}>
                {t('branch')}
              </Text>
              <Text fontWeight={500} fontSize={'sm'}>
                {order?.steps[0]?.branch_name}
              </Text>
            </Box>
          </Flex>
        ) : (
          <>
            <Flex gap={4} px={4} py={'11px'} alignItems={'center'}>
              <Box minW={'24px'}>
                <KitchenDetailsIcon color='#676767' />
              </Box>
              <Box>
                <Text color={'text.muted'} fontSize={'xs'}>
                  {t('where_from')}
                </Text>
                <Text fontWeight={500} fontSize={'sm'}>
                  {order?.steps[0]?.branch_name}
                </Text>
              </Box>
            </Flex>
            <Flex gap={4} px={4} py={'11px'} alignItems={'center'}>
              <Box minW={'24px'}>
                <Home2Icon color='#676767' />
              </Box>
              <Box>
                <Text color={'text.muted'} fontSize={'xs'}>
                  {t('where')}
                </Text>
                <Text fontWeight={500} fontSize={'sm'}>
                  {order?.to_address}
                </Text>
              </Box>
            </Flex>
          </>
        )}

        {order?.delivery_type === 'hall' && (
          <Flex gap={4} px={4} py={'11px'} alignItems={'center'}>
            <CrmTableIcon color='#676767' />
            <Box>
              <Text color={'text.muted'} fontSize={'xs'}>
                {t('table_number')}
              </Text>
              <Text fontWeight={500} fontSize={'sm'}>
                {order?.crm_table_number}
              </Text>
            </Box>
          </Flex>
        )}

        <Box px='4' py='1'>
          {order?.to_address_description && (
            <Flex
              flexDir={'column'}
              gap={3}
              bg={'background'}
              p={4}
              rounded={16}
            >
              <Text fontSize={'sm'} fontWeight={500}>
                {t('comment_to_courier')}
              </Text>
              <Text fontSize={'sm'} fontWeight={400}>
                {order?.description}
              </Text>
            </Flex>
          )}
        </Box>

        <Flex
          px={4}
          py={2}
          fontWeight={500}
          fontSize={'sm'}
          justifyContent={'space-between'}
        >
          <Text>{t('payment_type')}</Text>
          <Text>{order.payment_type_obj?.name[currentLocale]}</Text>
        </Flex>
        {order?.payment_type === 'cash' && change > 0 && (
          <HStack
            px={4}
            py={2}
            fontSize={'sm'}
            fontWeight={500}
            justifyContent={'space-between'}
          >
            <Text>{t('checkout.exchange')}</Text>
            <NumberToPrice unstyled value={change} />
          </HStack>
        )}
        <HStack
          px={4}
          py={2}
          fontSize={'sm'}
          fontWeight={500}
          justifyContent={'space-between'}
        >
          <Text>{t('cost_of_goods')}</Text>
          <NumberToPrice unstyled value={order?.steps?.[0]?.step_amount || 0} />
        </HStack>
        {order?.service_fee > 0 && (
          <HStack
            px={4}
            py={2}
            fontSize={'sm'}
            fontWeight={500}
            justifyContent={'space-between'}
          >
            <Text>{t('checkout.service_fee')}</Text>
            <NumberToPrice unstyled value={order.service_fee} />
          </HStack>
        )}

        {order?.delivery_type === 'delivery' && (
          <HStack
            px={4}
            py={2}
            fontSize={'sm'}
            fontWeight={500}
            justify='space-between'
          >
            <Stack gap={1}>
              <Text>{t('cost_of_delivery')}</Text>
            </Stack>
            <NumberToPrice
              unstyled
              freeText={t('free_delivery')}
              isFreeTextVisible={order?.is_delivery_free}
              value={order?.is_delivery_free ? 0 : order?.delivery_price}
            />
          </HStack>
        )}
        {order?.payment?.map((payment) => {
          return (
            payment?.payment_type === 'cashback' && (
              <HStack
                px={4}
                py={2}
                justify='space-between'
                fontSize={'sm'}
                fontWeight={500}
                key={payment?.id}
              >
                <Text>
                  {payment?.payment_type === 'cashback'
                    ? t('cashback_title')
                    : payment?.payment_type_obj?.name?.[currentLocale]}
                </Text>
                <Flex gap={1}>
                  -
                  <NumberToPrice value={payment?.paid_amount} />
                </Flex>
              </HStack>
            )
          );
        })}
        {order?.discounts?.map((discount) => (
          <HStack px={4} py={2} justify='space-between' key={discount.id}>
            <Text>{discount?.name?.[currentLocale]}</Text>

            <NumberToPrice value={discount?.price || 0} />
          </HStack>
        ))}
        <HStack
          px={4}
          py={2}
          justify='space-between'
          borderTop='1px solid'
          borderColor='gray.200'
        >
          <Text fontWeight={400} fontSize='md'>
            {t('total')}
          </Text>
          <Box fontWeight={400} fontSize='md' textAlign='right'>
            {(order?.discount_price !== 0 ||
              order?.steps?.[0]?.step_discount_price !== 0) && (
              <Text
                as='span'
                fontSize='sm'
                color='text.muted'
                fontWeight={400}
                textDecoration='line-through'
              >
                {numToPrice(
                  total -
                    order?.discount_price -
                    (order?.steps?.[0]?.step_discount_price || 0) +
                    (+order?.service_fee || 0),
                )}
              </Text>
            )}
            <NumberToPrice
              textProps={{
                fontWeight: 500,
              }}
              spanProps={{
                fontWeight: 500,
                fontSize: 16,
              }}
              value={total + (+order?.service_fee || 0)}
            />
          </Box>
        </HStack>
      </Stack>
    </Box>
  );
};

export default Mobile;
